import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Unlink, Users } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { CallDetail } from "../../../lib/calls";
import type { LeadStatus } from "../../../lib/clients";
import { PartyPicker, type PartyRef } from "../_shared/PartyPicker";
import { IconBtn } from "../../ui";
import { CallLeadStatus } from "./CallLeadStatus";
import { CallPerson } from "./CallPerson";

/**
 * Who the call is filed against, as one value.
 *
 * The client wins when both columns are set, the same order `ticketParty` uses:
 * the customer relationship is the more specific fact. Exported because the
 * pane above states this as a header fact and only opens the editor below when
 * somebody asks for it.
 */
export function callParty(call: CallDetail): PartyRef | null {
  return call.client
    ? { kind: "client", id: call.client.id, name: call.client.name }
    : call.lead
      ? { kind: "lead", id: call.lead.id, name: call.lead.name }
      : null;
}

/** Where a party's own card lives. */
export function partyPath(party: PartyRef): string {
  return party.kind === "lead" ? `/leads/${party.id}` : `/clients/${party.id}`;
}

/**
 * Who the call turned out to be about.
 *
 * `regCode` and `leadDomain` already say where the number was picked up, but
 * those are where it came FROM. A call placed to a company in the register may
 * be about a client who is already in the CRM under a different name, and until
 * an operator can say so, the call is invisible from the client's side.
 *
 * The picker searches clients AND juhukliendid, which it did not always. It
 * used to be the same `ClientPicker` the mail thread uses, and that made the
 * commonest case on an inbound line unreachable: somebody rings once, they are
 * nobody's client, and the only way to attach them was to promote them to a
 * full customer first. The voicemail intake now creates a lead for exactly that
 * person, so the control that attaches one has to be able to find it.
 *
 * Which of the two ids gets written is decided by which row was picked - never
 * by the operator choosing a mode first.
 */
export function CallLinks({
  call,
  canMoveLead,
  canReadClients,
  onPatch,
  onLeadMoved,
}: {
  call: CallDetail;
  /** Moving a lead is another module's permission (manage:crm.clients). */
  canMoveLead: boolean;
  /** Listing a company's people is a read in the clients module (`view:crm`),
   *  so the person control is hidden without it rather than failing with a 403
   *  the moment somebody opens it. The person already ON the row still shows -
   *  that name arrived with the call. */
  canReadClients: boolean;
  onPatch: (body: {
    clientId?: string | null;
    leadId?: string | null;
    contactId?: string | null;
  }) => Promise<void>;
  onLeadMoved: (status: LeadStatus) => void;
}) {
  const t = useT();
  const attached = callParty(call);
  const [name, setName] = useState(attached?.name ?? "");

  return (
    <div className="flex flex-wrap items-center gap-2.5 border-b border-[var(--crm-hair)] px-6 py-3">
      <span className="flex min-w-[220px] flex-1 items-center gap-2">
        <Users className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <PartyPicker
            value={name}
            selected={attached}
            onChange={setName}
            onPick={(picked) => {
              setName(picked.name);
              // Both keys travel, so switching a call from a lead to a client
              // clears the one that is no longer true. Sending only the new id
              // would leave a row pointing at two different companies.
              void onPatch(
                picked.kind === "client"
                  ? { clientId: picked.id, leadId: null }
                  : { leadId: picked.id, clientId: null },
              );
            }}
            placeholder={t("calls.detail.partyPlaceholder")}
          />
        </span>
      </span>

      {/* Who at that company picked up. Only on a call filed against a client:
          contacts hang off clients, and a juhuklient IS the person. It sits
          straight after the company because that is the order the two are
          decided in - and because the server will not accept the second without
          the first. */}
      {call.client && canReadClients ? (
        <CallPerson
          clientId={call.client.id}
          contact={call.contact}
          onPick={(contactId) => void onPatch({ contactId })}
        />
      ) : null}

      {/* The call is what moves a lead through the funnel, so the stage is
          changed here rather than on a board somebody has to go and find. Only
          for a lead - a client is past the pipeline. */}
      {call.lead && canMoveLead ? (
        <CallLeadStatus leadId={call.lead.id} status={call.lead.status} onMoved={onLeadMoved} />
      ) : null}

      {/* The picker already shows the name, so this is a way THERE, not a second
          copy of it. */}
      {attached ? (
        <>
          <Link
            to={partyPath(attached)}
            className="crm-cb"
            title={t(attached.kind === "lead" ? "calls.detail.openLead" : "calls.detail.openClient")}
            aria-label={t(attached.kind === "lead" ? "calls.detail.openLead" : "calls.detail.openClient")}
          >
            <ArrowUpRight className="size-4" />
          </Link>
          <IconBtn
            title={t("calls.detail.unlinkClient")}
            aria-label={t("calls.detail.unlinkClient")}
            onClick={() => {
              setName("");
              void onPatch({ clientId: null, leadId: null });
            }}
          >
            <Unlink className="size-4" />
          </IconBtn>
        </>
      ) : null}
    </div>
  );
}
